import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp, Loader2, Minus, MessageSquare, Plus, X } from 'lucide-react';
import { api } from '../../utils/api';
import { streamChatMessage } from '../chat/chatStream';
import { renderMarkdown } from '../chat/markdown';
import '../chat/Chat.css';

// The Movers screen's docked "Ask" panel — a slim chat scoped to the
// movers currently on screen. It rides the same backend as the
// full-page Chat (a real thread + the SSE stream); the scoping is done
// by prefixing the thread's first message with a summary of the
// on-screen movers. Kept to this page only by design.
//
// The thread id is remembered across mounts. The turns were always
// persisted server-side, but the id lived in a ref, so collapsing the
// panel or leaving the page stranded the conversation: the panel came
// back empty and the next question opened a brand-new thread, leaving a
// pile of one-line "Movers — ask" threads in the Chat tab. Remembering
// it means the panel resumes where it left off and "Open in Chat"
// lands on the same conversation.

const STROKE = 2.75;
const THREAD_KEY = 'movers_ask_thread';

function moversContext(regions) {
  const parts = regions
    .filter((region) => region.movers.length)
    .map((region) => {
      const list = region.movers
        .map((m) => (m.flat
          ? `${m.name} (${m.category}, not growing this week)`
          : `${m.name} (${m.category}, +${m.growth_pct}%${m.baseline_multiple ? `, ${m.baseline_multiple}× baseline` : ''})`))
        .join('; ');
      return `${region.label}: ${list}`;
    });
  const text = `Context (not shown to the user): the Movers screen currently shows these brands breaking out against their own four-week baseline. ${parts.join('. ')}. Answer about these brands unless asked otherwise.`;
  // The API caps context at 4000 chars.
  return text.length > 3900 ? `${text.slice(0, 3900)}…` : text;
}

function AskPanel({ regions, sheet, onCollapse }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [phase, setPhase] = useState(null);
  const [error, setError] = useState(null);
  const [threadId, setThreadId] = useState(() => {
    const stored = localStorage.getItem(THREAD_KEY);
    return stored ? Number(stored) : null;
  });
  const threadIdRef = useRef(null);
  const abortRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const rememberThread = useCallback((id) => {
    threadIdRef.current = id;
    setThreadId(id);
    if (id) localStorage.setItem(THREAD_KEY, String(id));
    else localStorage.removeItem(THREAD_KEY);
  }, []);

  // Resume the remembered conversation. Tool rows carry no prose, so
  // only user/assistant turns come back into the panel.
  useEffect(() => {
    const stored = localStorage.getItem(THREAD_KEY);
    if (!stored) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getChatThread(Number(stored));
        if (cancelled) return;
        threadIdRef.current = Number(stored);
        setMessages(
          (data.messages || [])
            .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content_md)
            .map((m) => ({ role: m.role, content: m.content_md })),
        );
      } catch {
        // Deleted from the Chat tab, or belongs to another org after a
        // tenant switch — forget it and start clean rather than erroring.
        if (!cancelled) {
          localStorage.removeItem(THREAD_KEY);
          setThreadId(null);
          threadIdRef.current = null;
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startNew = () => {
    if (streaming) return;
    abortRef.current?.abort();
    rememberThread(null);
    setMessages([]);
    setError(null);
  };

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, phase]);

  const send = async () => {
    const text = draft.trim();
    if (!text || streaming) return;
    setDraft('');
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setStreaming(true);
    setPhase('Thinking…');

    try {
      if (!threadIdRef.current) {
        const thread = await api.createChatThread('Movers — ask');
        rememberThread(thread.id ?? thread.thread?.id);
      }

      const controller = new AbortController();
      abortRef.current = controller;
      let assistantText = '';
      const upsertAssistant = (body) => {
        setMessages((prev) => {
          const next = [...prev];
          if (next.length && next[next.length - 1].role === 'assistant' && next[next.length - 1].streaming) {
            next[next.length - 1] = { role: 'assistant', content: body, streaming: true };
          } else {
            next.push({ role: 'assistant', content: body, streaming: true });
          }
          return next;
        });
      };

      // Fresh screen context rides along on every turn (model-only —
      // the stored thread keeps just what was typed).
      await streamChatMessage({
        threadId: threadIdRef.current,
        content: text,
        context: moversContext(regions),
        signal: controller.signal,
        onEvent: (evt) => {
          if (evt.type === 'token') {
            assistantText += evt.text || '';
            upsertAssistant(assistantText);
          } else if (evt.type === 'thinking') {
            setPhase(evt.detail || evt.phase || 'Thinking…');
          } else if (evt.type === 'tool_call_started') {
            setPhase(`Running ${evt.name || 'a lookup'}…`);
          } else if (evt.type === 'tool_call_finished') {
            setPhase(`Finished ${evt.name || 'lookup'}`);
          } else if (evt.type === 'final') {
            assistantText = evt.content_md || assistantText;
            upsertAssistant(assistantText);
          } else if (evt.type === 'error') {
            setError(evt.message || 'Something went wrong.');
          }
        },
      });
      // Mark the reply finished so the next turn starts a new bubble.
      setMessages((prev) => prev.map((m) => (m.streaming ? { role: m.role, content: m.content } : m)));
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
    } finally {
      setStreaming(false);
      setPhase(null);
      abortRef.current = null;
    }
  };

  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ margin: 0 }}>Ask</h4>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            className="btn btn-icon btn-secondary"
            title="New conversation"
            aria-label="Start a new conversation"
            onClick={startNew}
            disabled={streaming || (!threadId && messages.length === 0)}
          >
            <Plus size={16} strokeWidth={STROKE} />
          </button>
          {/* Minimise and close do the same thing — the sheet only needs
              one of them. */}
          {!sheet && (
            <button type="button" className="btn btn-icon btn-secondary" title="Minimise" aria-label="Minimise panel" onClick={onCollapse}>
              <Minus size={16} strokeWidth={STROKE} />
            </button>
          )}
          <button type="button" className="btn btn-icon btn-secondary" title="Close" aria-label="Close panel" onClick={onCollapse}>
            <X size={16} strokeWidth={STROKE} />
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 12, opacity: 0.65 }}>Scoped to the movers on screen.</p>
        {threadId && (
          <Link
            to={`/chat?thread=${threadId}`}
            style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <MessageSquare size={12} strokeWidth={STROKE} /> Open in Chat
          </Link>
        )}
      </div>

      <div ref={listRef} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', flex: 1, overflowY: 'auto' }}>
        {messages.length === 0 && (
          <p style={{ margin: 0, fontSize: 13, opacity: 0.6 }}>
            Ask why a brand is moving, what's similar in another region, or who to contact first.
          </p>
        )}
        {messages.map((m, i) => (
          m.role === 'user' ? (
            <div
              key={i}
              style={{
                alignSelf: 'flex-end', maxWidth: '85%',
                background: 'var(--color-accent)', color: 'var(--color-bg)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md) var(--radius-md) 4px var(--radius-md)',
                fontSize: 13, whiteSpace: 'pre-wrap',
              }}
            >
              {m.content}
            </div>
          ) : (
            <div
              key={i}
              className="chat-bubble-markdown"
              style={{
                alignSelf: 'flex-start', maxWidth: '100%', minWidth: 0,
                background: 'var(--color-bg)',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md) var(--radius-md) var(--radius-md) 4px',
                fontSize: 13, gap: 'var(--space-2)',
              }}
            >
              {renderMarkdown(m.content)}
            </div>
          )
        ))}
        {streaming && phase && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, opacity: 0.65 }}>
            <Loader2 size={13} strokeWidth={STROKE} className="spin" /> {phase}
          </div>
        )}
        {error && (
          <div style={{ fontSize: 12, color: 'var(--color-accent-700)' }}>Error: {error}</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <input
          className="input"
          placeholder="Ask about a brand or a region…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          disabled={streaming}
          aria-label="Ask about the movers on screen"
        />
        <button
          type="button"
          className="btn btn-primary btn-icon"
          title="Send"
          aria-label="Send"
          onClick={send}
          disabled={streaming || !draft.trim()}
        >
          <ArrowUp size={16} strokeWidth={STROKE} />
        </button>
      </div>
    </>
  );

  // On a phone the panel can't dock beside the feed — it comes up as a
  // sheet over it, dismissed by the scrim or either header button.
  if (sheet) {
    return (
      <>
        <div className="o-sheet-backdrop" onClick={onCollapse} />
        <aside
          className="o-sheet"
          style={{
            height: '82dvh', background: 'var(--color-surface)',
            padding: 'var(--space-3) var(--space-4) calc(var(--space-4) + env(safe-area-inset-bottom, 0px))',
            gap: 'var(--space-3)',
          }}
        >
          <span className="o-sheet-grip" style={{ flex: 'none' }} />
          {body}
        </aside>
      </>
    );
  }

  return (
    <aside
      style={{
        width: 380, flex: 'none', background: 'var(--color-surface)',
        padding: 'var(--space-4)', display: 'flex', flexDirection: 'column',
        gap: 'var(--space-3)', position: 'sticky', top: 0, height: '100vh',
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      {body}
    </aside>
  );
}

export default AskPanel;
